import { alpha } from "@mui/material";
import { darken, type Theme } from "@mui/material/styles";
import { metadataIconColors, promoOfferColors } from "@/theme/tokens";

/**
 * Standard keyboard focus outline for interactive surfaces that use the selection ring.
 * Prefer spreading into `&:focus-visible` (not every component uses the same offset).
 */
export function selectionFocusVisibleOutline(theme: Theme, outlineOffsetPx = 2) {
  const ring = theme.palette.custom.selection.ring;
  return {
    outline: `2px solid ${ring}`,
    outlineOffset: outlineOffsetPx,
  } as const;
}

/**
 * Ergonomic, theme-aware accessor for centralized design tokens. Components import
 * this helper and read `tokens.selection.*` / `tokens.navActive.*` instead of
 * computing primary/secondary alpha math locally. Dark mode resolves automatically.
 */
export const getDesignTokens = (theme: Theme) => ({
  accent: theme.palette.primary.main,
  onAccent: theme.palette.primary.contrastText,
  surface: theme.palette.background.paper,
  /** Subtle list/hover tint. See `palette.custom.surfaceSoft`. */
  surfaceSoft: theme.palette.custom.surfaceSoft,
  /** Soft horizontal/vertical divider for flowing list rows. See `palette.custom.separatorSubtle`. */
  separatorSubtle: theme.palette.custom.separatorSubtle,
  textPrimary: theme.palette.text.primary,
  border: theme.palette.divider,

  button: {
    /** Brand/selection chrome — not the primary-action CTA contract. */
    primary: {
      bg: theme.palette.primary.main,
      text: theme.palette.primary.contrastText,
      hover: theme.palette.primary.dark,
    },
    neutral: {
      bg: theme.palette.background.paper,
      text: theme.palette.text.primary,
      border: theme.palette.divider,
      hover: alpha(theme.palette.text.primary, 0.04),
    },
  },

  chip: {
    default: {
      bg: theme.palette.background.paper,
      text: theme.palette.text.primary,
      border: theme.palette.divider,
    },
    active: {
      bg: theme.palette.primary.main,
      text: theme.palette.primary.contrastText,
    },
  },

  tag: {
    veg: {
      color: theme.palette.success.main,
      bg: alpha(theme.palette.success.main, 0.1),
    },
    nonVeg: {
      color: theme.palette.error.main,
      bg: alpha(theme.palette.error.main, 0.1),
    },
    bestseller: {
      color: theme.palette.warning.main,
      bg: alpha(theme.palette.warning.main, 0.1),
    },
    spicy: {
      color: theme.palette.error.main,
      bg: alpha(theme.palette.error.main, 0.1),
    },
    /**
     * Catalog promotion / offer chip (DiscountChip + TagChip `promo`).
     * Rich Premium Gold — platform semantic, not tenant Theme Color or CTA fill.
     */
    promo: {
      color:
        theme.palette.mode === "light"
          ? promoOfferColors.text
          : alpha(promoOfferColors.bg, 0.95),
      icon:
        theme.palette.mode === "light"
          ? promoOfferColors.icon
          : alpha(promoOfferColors.bg, 0.92),
      bg:
        theme.palette.mode === "light"
          ? promoOfferColors.bg
          : alpha(promoOfferColors.border, 0.2),
      border:
        theme.palette.mode === "light"
          ? promoOfferColors.border
          : alpha(promoOfferColors.border, 0.7),
      hoverBg:
        theme.palette.mode === "light"
          ? darken(promoOfferColors.bg, 0.04)
          : alpha(promoOfferColors.border, 0.28),
      hoverBorder:
        theme.palette.mode === "light"
          ? darken(promoOfferColors.border, 0.08)
          : alpha(promoOfferColors.border, 0.85),
      pressedBg:
        theme.palette.mode === "light"
          ? darken(promoOfferColors.bg, 0.08)
          : alpha(promoOfferColors.border, 0.34),
      pressedBorder:
        theme.palette.mode === "light"
          ? darken(promoOfferColors.border, 0.14)
          : alpha(promoOfferColors.border, 0.95),
      disabledBg:
        theme.palette.mode === "light"
          ? alpha(promoOfferColors.bg, 0.55)
          : alpha(promoOfferColors.border, 0.12),
      disabledBorder:
        theme.palette.mode === "light"
          ? alpha(promoOfferColors.border, 0.45)
          : alpha(promoOfferColors.border, 0.28),
      disabledColor:
        theme.palette.mode === "light"
          ? alpha(promoOfferColors.text, 0.42)
          : alpha(promoOfferColors.bg, 0.42),
      disabledIcon:
        theme.palette.mode === "light"
          ? alpha(promoOfferColors.icon, 0.38)
          : alpha(promoOfferColors.bg, 0.38),
      borderWidthPx: 1,
      shadow:
        theme.palette.mode === "light" ? promoOfferColors.shadow : "none",
      hoverShadow:
        theme.palette.mode === "light" ? promoOfferColors.hoverShadow : "none",
      pressedShadow: "none",
      /** Target ~26px — do not grow when padding/icon is tuned. */
      minHeightPx: 26,
      /** Breathing room under the price row (PriceDisplay only). */
      gapFromPricePx: 8,
      letterSpacing: "0.02em",
      fontSizePx: { compact: 11, default: 12 },
      /**
       * Optical horizontal padding: slightly more on the leading edge
       * (icon side) so the pill feels balanced with the trailing text edge.
       */
      paddingLeftPx: { compact: 12, default: 16 },
      paddingRightPx: { compact: 12, default: 14 },
      paddingYPx: { compact: 4, default: 5 },
      /** Subtle accent — text remains primary. */
      iconSizePx: 12,
      iconGapPx: 4,
      /** Optical nudge so stroke icons sit with uppercase label. */
      iconOpticalOffsetYPx: 0.5,
    },
  },

  /**
   * Quiet allergen / dietary / spice metadata — inline text, not pill chrome.
   * Intentionally lighter than promo chips and primary CTAs.
   * Icon accents are muted semantic colors; label text stays `color` (neutral).
   */
  metadataInline: {
    color: theme.palette.text.secondary,
    separatorColor: theme.palette.text.disabled,
    fontSizePx: { compact: 12, default: 12.5 },
    fontWeight: theme.typography.fontWeightMedium,
    iconGapPx: 4,
    /** Target 12–14px — match Search/Filter stroke weight at metadata scale. */
    iconSizePx: 14,
    itemGapPx: 8,
    separatorPx: 6,
    /**
     * Semantic icon accents only. Fallback `neutral` = label color (non-dominant).
     * Dark mode: slightly lifted for legibility on dark surfaces.
     */
    iconAccent: {
      spicy:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.spicy, 0.92)
          : metadataIconColors.spicy,
      gluten:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.gluten, 0.92)
          : metadataIconColors.gluten,
      soy:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.soy, 0.92)
          : metadataIconColors.soy,
      dairy:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.dairy, 0.92)
          : metadataIconColors.dairy,
      nuts:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.nuts, 0.92)
          : metadataIconColors.nuts,
      seafood:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.seafood, 0.92)
          : metadataIconColors.seafood,
      egg:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.egg, 0.92)
          : metadataIconColors.egg,
      vegetarian:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.vegetarian, 0.92)
          : metadataIconColors.vegetarian,
      vegan:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.vegan, 0.92)
          : metadataIconColors.vegan,
      mild:
        theme.palette.mode === "dark"
          ? alpha(metadataIconColors.mild, 0.92)
          : metadataIconColors.mild,
      neutral: theme.palette.text.secondary,
    },
  },

  /**
   * Selection semantic tokens — for "currently selected by user" UI.
   * Source of truth: `palette.custom.selection` (mode-aware, see `create-theme.ts`).
   *
   * Use these everywhere a user picks from a set of options. Do NOT use `success.*` for
   * selection — `success` is reserved for applied/completed/confirmed feedback.
   */
  selection: {
    hoverSurface: theme.palette.custom.selection.hoverSurface,
    surfaceFaint: theme.palette.custom.selection.surfaceFaint,
    surfaceSubtle: theme.palette.custom.selection.surfaceSubtle,
    surfaceTinted: theme.palette.custom.selection.surfaceTinted,
    surfaceSolid: theme.palette.custom.selection.surfaceSolid,
    borderSoft: theme.palette.custom.selection.borderSoft,
    border: theme.palette.custom.selection.border,
    borderStrong: theme.palette.custom.selection.borderStrong,
    text: theme.palette.custom.selection.text,
    textOnSolid: theme.palette.custom.selection.textOnSolid,
    ring: theme.palette.custom.selection.ring,
    focusOutline: theme.palette.custom.selection.focusOutline,
  },

  /** Active-navigation tokens (anchored to `secondary.main`). */
  navActive: {
    surface: theme.palette.custom.navActive.surface,
    border: theme.palette.custom.navActive.border,
    text: theme.palette.custom.navActive.text,
  },
});

/**
 * Semantic surface usage (selection vs success vs navigation)
 * -----------------------------------------------------------
 *
 * **selection.\*** — User intent / picked option / keyboard cursor on an option list:
 * - `hoverSurface` — Hover before commit (light primary tint).
 * - `surfaceFaint` — Large shells (order summary panels).
 * - `surfaceSubtle` — Selected combo row, filter chips (filled idle secondary band).
 * - `surfaceTinted` — Selected payment row, modifier row, address-selected fill.
 * - `surfaceSolid` — Solid chip/tab (“active” meal type, filter chip on).
 * - `borderSoft` / `border` / `borderStrong` — From faint outline to strong selected edge.
 * - `ring` / `focusOutline` — Keyboard focus outline vs softer focused control outline.
 *
 * **success.\*** — Applied discount, paid badge, savings confirmed — never for “which row is focused”.
 *
 * **navActive.\*** — Current route in bottom navigation only (anchored to text primary / secondary palette).
 *
 * Do **not** use `alpha(theme.palette.primary.main, …)` in components for selection surfaces;
 * extend tokens in `create-theme.ts` if a new band is needed.
 */
